import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerService } from 'src/app/data/worker.service';
import { DocumentFileDto } from 'src/app/models/dtos/document-file-dto';
import { WorkerFileTypeDto } from 'src/app/models/dtos/worker-file-type-dto';
import { WorkerTypeSectionEnum } from 'src/app/models/enums/worker-type-section-enum';
import { FileService } from 'src/app/shared/services/file-service';
import { AddDocumentModalComponent, AddDocumentModalData } from '../add-document-modal/add-document-modal.component';

@Component({
  selector: 'app-additional-documents-section',
  templateUrl: './additional-documents-section.component.html',
  styleUrls: ['./additional-documents-section.component.scss'],
  standalone: false,
})
export class AdditionalDocumentsSectionComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private workerService = inject(WorkerService);
  private dictionaryService = inject(DictionaryService);
  private spinner = inject(NgxSpinnerService);
  private fileService = inject(FileService);

  @Input() workerId: number;
  @Input() readonly = false;

  documentFiles$ = new BehaviorSubject<DocumentFileDto[]>([]);
  private fileTypes: WorkerFileTypeDto[] = [];

  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.documentFiles$.complete();
  }

  private loadData(): void {
    this.spinner.show();
    this.workerService
      .getWorkerProfileDocuments(this.workerId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((docs) => {
        this.documentFiles$.next(
          docs.DocumentFiles.filter((df) => df.SectionId === WorkerTypeSectionEnum.AdditionalFilesSection),
        );
      });

    this.dictionaryService
      .getWorkerFileTypes(this.workerId)
      .pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe((types) => {
        this.fileTypes = types.filter((t) => t.SectionId === WorkerTypeSectionEnum.AdditionalFilesSection);
      });
  }

  openModal(): void {
    const documentFiles = this.documentFiles$.getValue();
    const fileTypesWithAdjustedLimits = this.fileTypes.map((fileType) => {
      const res = { ...fileType };
      res.CountLimit -=
        documentFiles
          .find((df) => df.SectionId === WorkerTypeSectionEnum.AdditionalFilesSection)
          ?.SectionFiles.find((f) => f.FileTypeId === fileType.Id)?.WorkerFiles?.length ?? 0;
      return res;
    });

    const config = new MatDialogConfig<AddDocumentModalData>();
    config.panelClass = 'document-form-dialog';
    config.data = {
      Title: 'AdditionalDocuments',
      WorkerId: this.workerId,
      FileTypes: fileTypesWithAdjustedLimits,
    };

    this.dialog
      .open(AddDocumentModalComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isSuccess: boolean) => {
        if (isSuccess) {
          this.loadData();
        }
      });
  }

  showFile(id: number, name: string, absenceId?: number): void {
    const endpoint = absenceId ? `absences/${absenceId}/files` : `workers/${this.workerId}/files`;
    this.fileService.showFile(name, id, endpoint);
  }

  downloadFile(id: number, name: string, absenceId?: number): void {
    const endpoint = absenceId ? `absences/${absenceId}/files` : `workers/${this.workerId}/files`;
    this.fileService.downloadFile(id, name, endpoint);
  }

  deleteFile(fileId: number): void {
    const config = <Type>(data: Type): MatDialogConfig<Type> => {
      const dialogConfig = new MatDialogConfig<Type>();
      dialogConfig.panelClass = 'document-form-dialog';
      dialogConfig.data = data;
      return dialogConfig;
    };
    this.fileService.deleteFile(fileId, config).subscribe(() => this.loadData());
  }
}
